import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { parseAllZoneEvents } from '../_utils/parseZoneEvents'
import { useEmitterVisibility } from './_utils/useEmitterVisibility'
import { EmitterVisibilityToggles } from './EmitterVisibilityToggles'
import { IrrigationPlannerTableRows } from './IrrigationPlannerTableRows'
import type { TCombinedTableProps } from './types'

export const IrrigationPlannerTable = ({
  eventsResponse,
  thisWeekDates,
  nextWeekDates,
  dailyEtNextWeek,
  dailyEtLastWeek,
}: TCombinedTableProps) => {
  const depthUnit = unitConverter.evapotranspiration().suffix()
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]

  const emitterTypes = useMemo(
    () => [...new Set(eventsResponse?.map((ze) => ze.emitterType) ?? [])] as TEmitterType[],
    [eventsResponse],
  )

  const parsedEvents = useMemo(
    () => (selectedProperty?.timezone ? parseAllZoneEvents(eventsResponse, selectedProperty.timezone) : {}),
    [eventsResponse, selectedProperty?.timezone],
  )

  const { visibleEmitters, toggleEmitterVisibility } = useEmitterVisibility(emitterTypes)

  return (
    <div>
      <h3>{translate.phrases.banyanApp('Irrigation Planning Table')}</h3>

      <div css={{ marginTop: 16, marginBottom: 16 }}>
        <EmitterVisibilityToggles
          emitterTypes={emitterTypes}
          visibleEmitters={visibleEmitters}
          onToggle={toggleEmitterVisibility}
        />
      </div>

      {emitterTypes
        .filter((et) => visibleEmitters[et])
        .map((emitterType) => (
          <div key={emitterType} css={{ marginBottom: 32 }}>
            <h4 css={{ marginBottom: 16, textDecoration: 'underline' }}>
              {mapApiEmitterTypeToLabel(emitterType)}
            </h4>

            <IrrigationPlannerTableRows
              eventsResponse={eventsResponse}
              parsedEvents={parsedEvents}
              dailyEtData={dailyEtLastWeek}
              emitterType={emitterType}
              dateRange={thisWeekDates}
              showApplied
            />
            <IrrigationPlannerTableRows
              eventsResponse={eventsResponse}
              parsedEvents={parsedEvents}
              dailyEtData={dailyEtNextWeek}
              emitterType={emitterType}
              dateRange={nextWeekDates}
            />

            <div css={{ marginTop: 8, fontSize: 12, color: colors.grey800 }}>
              <p>
                {translate.phrases.banyanApp('* All values in {{unit}}', {
                  unit: depthUnit === 'in' ? 'inches' : 'millimeters',
                })}
              </p>
              <p>
                {translate.phrases.banyanApp(
                  '* Planned Balance = ETc - Rain - Planned (scheduled irrigation)',
                )}
              </p>
              <p>
                {translate.phrases.banyanApp('* Applied Balance = ETc - Rain - Applied (actual irrigation)')}
              </p>
            </div>
          </div>
        ))}
    </div>
  )
}
